import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { PowerBIReportComponent } from './components/powerbi-report/powerbi-report.component';
import { PowerBIService, ReportInfo } from '../../services/powerbi.service';
import { AuthService, User } from '../../services/auth.service';

/**
 * Página principal de KPIs con Power BI
 *
 * Muestra dashboards de Power BI según el perfil del usuario:
 * - Gerencia: Dashboard ejecutivo
 * - Operaciones: Dashboard operacional
 * - Clientes: Dashboard de cliente
 */
@Component({
    selector: 'app-kpi-page',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatTabsModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        PowerBIReportComponent,
    ],
    template: `
        <div class="kpi-page">
            <!-- Header -->
            <div class="page-header">
                <div class="header-content">
                    <h1>
                        <mat-icon>analytics</mat-icon>
                        {{ pageTitle }}
                    </h1>
                    <p class="subtitle">
                        Visualización de indicadores clave de rendimiento
                    </p>
                </div>
            </div>

            <!-- Loading state -->
            <div *ngIf="isLoading" class="loading-container">
                <mat-spinner diameter="50"></mat-spinner>
                <p>Cargando reportes...</p>
            </div>

            <!-- Not configured state -->
            <mat-card
                *ngIf="!isLoading && !isConfigured"
                class="status-card warning"
            >
                <mat-card-content>
                    <mat-icon>warning</mat-icon>
                    <div class="status-message">
                        <h3>Power BI no configurado</h3>
                        <p>
                            El servicio de Power BI no está configurado
                            correctamente. Contacte al administrador del
                            sistema.
                        </p>
                        <p class="tech-info">{{ statusMessage }}</p>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Main content -->
            <div *ngIf="!isLoading && isConfigured" class="kpi-content">
                <!-- Available reports list -->
                <mat-card
                    *ngIf="availableReports.length > 0"
                    class="reports-list"
                >
                    <mat-card-header>
                        <mat-card-title>
                            <mat-icon>list</mat-icon>
                            Reportes Disponibles
                        </mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                        <div class="reports-grid">
                            <div
                                *ngFor="let report of availableReports"
                                class="report-item"
                                [class.active]="report.id === selectedReportId"
                                (click)="selectReport(report)"
                            >
                                <mat-icon>assessment</mat-icon>
                                <div class="report-info">
                                    <span class="report-name">{{
                                        report.name
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </mat-card-content>
                </mat-card>

                <!-- Power BI Report -->
                <mat-card *ngIf="selectedReportId" class="report-card">
                    <mat-card-header>
                        <mat-card-title>
                            <mat-icon>dashboard</mat-icon>
                            {{ getSelectedReportName() }}
                        </mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                        <app-powerbi-report
                            [reportId]="selectedReportId"
                            [username]="
                                enableRLS ? selectedUsername : undefined
                            "
                            [roles]="enableRLS ? ['DEMOROLE'] : undefined"
                            [height]="'700px'"
                        >
                        </app-powerbi-report>
                    </mat-card-content>
                </mat-card>

                <!-- No report selected -->
                <mat-card
                    *ngIf="!selectedReportId && availableReports.length === 0"
                    class="status-card warning"
                >
                    <mat-card-content>
                        <mat-icon>info</mat-icon>
                        <div class="status-message">
                            <h3>Sin reportes disponibles</h3>
                            <p>
                                No tienes acceso a ningún reporte de Power BI.
                            </p>
                        </div>
                    </mat-card-content>
                </mat-card>
            </div>
        </div>
    `,
    styles: [
        `
            .kpi-page {
                padding: 24px;
                max-width: 1600px;
                margin: 0 auto;
            }

            .page-header {
                margin-bottom: 24px;
            }

            .header-content h1 {
                display: flex;
                align-items: center;
                gap: 12px;
                margin: 0;
                font-size: 28px;
                color: #333;
            }

            .header-content h1 mat-icon {
                font-size: 32px;
                width: 32px;
                height: 32px;
                color: #1976d2;
            }

            .subtitle {
                margin: 8px 0 0 44px;
                color: #666;
            }

            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
            }

            .loading-container p {
                margin-top: 16px;
                color: #666;
            }

            .status-card {
                margin-bottom: 24px;
            }

            .status-card.warning mat-card-content {
                display: flex;
                align-items: flex-start;
                gap: 16px;
                padding: 24px;
            }

            .status-card.warning mat-icon {
                font-size: 48px;
                width: 48px;
                height: 48px;
                color: #ff9800;
            }

            .status-message h3 {
                margin: 0 0 8px 0;
                color: #333;
            }

            .status-message p {
                margin: 0;
                color: #666;
            }

            .tech-info {
                margin-top: 8px !important;
                font-size: 12px;
                color: #999 !important;
            }

            .kpi-content {
                display: flex;
                flex-direction: column;
                gap: 24px;
            }

            .demo-selector mat-card-content {
                padding: 16px;
            }

            .demo-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 16px;
                color: #1976d2;
                font-weight: 500;
            }

            .demo-controls {
                display: flex;
                align-items: center;
                gap: 16px;
                flex-wrap: wrap;
            }

            .demo-controls mat-form-field {
                min-width: 200px;
            }

            .demo-controls .hint {
                margin: 0;
                color: #666;
                font-size: 13px;
            }

            .report-card mat-card-header {
                margin-bottom: 16px;
            }

            .report-card mat-card-title {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .reports-list mat-card-header {
                margin-bottom: 16px;
            }

            .reports-list mat-card-title {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .reports-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                gap: 12px;
            }

            .report-item {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 16px;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;
            }

            .report-item:hover {
                border-color: #1976d2;
                background-color: #f5f5f5;
            }

            .report-item.active {
                border-color: #1976d2;
                background-color: #e3f2fd;
            }

            .report-item mat-icon {
                color: #1976d2;
            }

            .report-info {
                display: flex;
                flex-direction: column;
            }

            .report-name {
                font-weight: 500;
                color: #333;
            }
        `,
    ],
})
export class KpiPageComponent implements OnInit {
    isLoading = true;
    isConfigured = false;
    statusMessage = '';
    currentUser: User | null = null;

    // Report settings
    selectedReportId = '';
    selectedUsername = ''; // Vacío = sin RLS
    enableRLS = false; // Por defecto sin RLS
    availableReports: ReportInfo[] = [];

    // Category from route data
    category?: string;
    pageTitle = 'KPIs - Power BI Dashboard';

    constructor(
        private powerBIService: PowerBIService,
        private authService: AuthService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.category = this.route.snapshot.data['category'];
        this.pageTitle =
            this.route.snapshot.data['pageTitle'] ||
            'KPIs - Power BI Dashboard';

        this.authService.currentUser$.subscribe((user) => {
            this.currentUser = user;
        });

        this.checkPowerBIStatus();
    }

    /**
     * Verifica el estado del servicio Power BI
     */
    private checkPowerBIStatus(): void {
        this.isLoading = true;

        this.powerBIService.getStatus().subscribe({
            next: (response) => {
                console.log('Power BI status response:', response);
                if (response.success && response.data) {
                    // El backend devuelve { powerbi: { configured, message }, reporteria: { ... } }
                    this.isConfigured = response.data.powerbi.configured;
                    this.statusMessage = response.data.powerbi.message;

                    if (this.isConfigured) {
                        this.loadAvailableReports();
                        return; // isLoading se apaga en loadAvailableReports
                    }
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error checking Power BI status:', err);
                this.isConfigured = false;
                this.statusMessage = 'Error al conectar con el servidor';
                this.isLoading = false;
            },
        });
    }

    /**
     * Carga la lista de reportes disponibles
     */
    private loadAvailableReports(): void {
        this.powerBIService.getReports(this.category).subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.availableReports = response.data;
                    // Seleccionar automáticamente el primer reporte disponible
                    if (
                        this.availableReports.length > 0 &&
                        !this.selectedReportId
                    ) {
                        this.selectedReportId = this.availableReports[0].id;
                    }
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading reports:', err);
                this.isLoading = false;
            },
        });
    }

    /**
     * Maneja el cambio de username para RLS
     */
    onUsernameChange(): void {
        // El componente PowerBIReport se recargará automáticamente
        // debido al binding de [username]
    }

    /**
     * Selecciona un reporte para mostrar
     */
    selectReport(report: ReportInfo): void {
        this.selectedReportId = report.id;
    }

    /**
     * Obtiene el nombre del reporte seleccionado
     */
    getSelectedReportName(): string {
        const report = this.availableReports.find(
            (r) => r.id === this.selectedReportId
        );
        return report?.name || 'Dashboard';
    }

    /**
     * Toggle RLS mode
     */
    toggleRLS(): void {
        this.enableRLS = !this.enableRLS;
        if (!this.enableRLS) {
            this.selectedUsername = '';
        } else {
            this.selectedUsername = 'A';
        }
    }
}
